import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import Icons from 'src/app/core/enums/icons.enum';
import { AuthService } from 'src/app/core/services/auth.service';
import { MenuItem } from 'src/portaljs/models/menu-item';
import { PortalApp } from 'src/app/core/shared/portal-app';
import { UserService } from 'src/app/core/services/user.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { ClientStateService } from 'src/app/core/services/client-state.service';
import { Notification } from 'src/portaljs/models/notification';
import * as _ from 'lodash-es';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { Application } from 'src/portaljs/constants/application';
import { DistrictModel } from 'src/app/core/models/sites.model';
import { ThemeService } from 'src/app/core/services/theme.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import PopupState from '../../core/enums/popup.state.enum';
import { ClientLoginService } from '../../core/services/client-login.service';
import { SidenavMenuStates } from 'src/app/core/enums/sidenav-menu-states.enum';
import { UnifiedSettingsService } from 'src/app/core/services/unified-settings.service';

const DestinyColors = {
  '--headerPrimaryFill-color': '#01338D',
  '--headerAccent-color': '#FFFFFF',
  '--border-color': '#B9B9B9',
  '--primaryFill-color': '#FFFFFF',
  '--secondaryFill-color': '#B9B9B9',
  '--accent-color': '#01338D',
  '--smallMediumText-color': '#000000',
  '--advancedSearchInputFieldBackground-color': '#FFFFFF',
  '--headerSmallMediumText-color': '#A1C3FF',
  '--mainBackground-color': '#F8FAFF',
  '--largeText-color': '#000000'
} as const;

const NewUNColors = {
  '--platform-900': '#001225',
  '--platform-800': '#001731',
  '--platform-700': '#001E3F',
  '--platform-500': '#002A59',
  '--platform-400': '#33557A',
  '--platform-300': '#547090',
  '--platform-200': '#8A9DB3',
  '--platform-100': '#B0BDCC',
  '--platform-50': '#E6EAEE',
  '--secondary-header': '#B0BDCC',
  '--base-white': '#FFFFFF',
  '--base-white-text': '#FFFFFF',
  '--f-blue-700': '#004197',
  '--f-blue-500': '#005BD4',
  '--f-blue-200': '#8AB4EB',
  '--font': 'DM sans'
} as const;

enum ItemsToHide {
  MyInfo = '(min-width: 1000px) and (max-width: 1199px)',
  Dashboard = '(min-width: 1000px) and (max-width: 1119px)',
  Home = '(min-width: 1000px) and (max-width: 1019px)'
}

@Component({
    selector: 'fss-portal-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    standalone: false
})
export class ToolbarComponent implements OnDestroy, OnInit {
  private ngUnsubscribe = new Subject();
  public readonly APPS = Application;

  public icons = Icons;
  public isGuestUser = false;
  public showLogin = true;
  public isShared = true;
  private sitesAvailable = 0;
  private districtUserType = false;

  public isLoginPopupDisplayed = false;
  public unreadCount = 0;

  private isHiddingMyInfo: boolean;
  private isHiddingDashboard: boolean;
  private isHiddingHome: boolean;

  public get isMobileMenuOpen(): boolean {
    return this.toolbarService.isMobileMenuOpen;
  }

  public isUnEnabledForCurrentApp() {
    return this.portalStateService.isUnEnabledForCurrentApp();
  }

  public closeSidenavPortal() {
    this.toolbarService.portal = undefined;
  }

  public get toolbarPortal() {
    return this.toolbarService.portal;
  }

  public get showSiteNotifications(): boolean {
    return !this.showLogin && !this.isShared;
  }
  public get isMessageSenderEnabled() {
    return this.envService.isMessageSenderEnabled();
  }
  public get isSecretsEnabled() {
    return this.envService.isSecretsEnabled();
  }
  public get isThemeChangerEnabled() {
    return this.envService.isThemePickerEnabled();
  }
  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }
  public get isDesktop(): boolean {
    return this.toolbarService.isDesktop();
  }
  public get showHamburguerMenu(): boolean {
    return this.toolbarService.getShowHambugerMenu();
  }

  public showUserProfile = true;
  public showAppSwitcher = true;
  public siteName = '';
  public siteGuid = '';
  public districtName = '';
  public originalStyles: any = {};
  public currentApp: Application;

  constructor(
    private portalStateService: PortalStateService,
    private authService: AuthService,
    private userService: UserService,
    private envService: EnvironmentService,
    private clientStateSvc: ClientStateService,
    private toolbarService: ToolbarService,
    private themeService: ThemeService,
    private readonly breakpointObserver: BreakpointObserver,
    private clientLoginService: ClientLoginService,
    private unifiedSettingsService: UnifiedSettingsService
  ) {}

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    this.observeScreenSizeChanges();
    this.clientStateSvc.getNotifications$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((notifications: Notification[]) => {
        const unreadNotifications: Notification[] = _.filter(
          notifications,
          (n: Notification) => n.new
        );
        this.unreadCount = unreadNotifications.length;
      });

    this.userService.getEnrolledSitesInDistrict$()
      .pipe(
        filter((x) => !!x),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((rez) => (this.sitesAvailable = rez.length));

    this.authService.getUserLogin$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((ulm) => {
        if (ulm) {
          this.districtUserType = (ulm.isDestinyAdmin() || ulm.isDistrictUser());
          this.isGuestUser = ulm.isGuestUser();
          this.showLogin = this.isGuestUser;
          this.isShared = ulm.isSharedUser();
          this.showUserProfile = !ulm.isGuestUser();
          this.showAppSwitcher = true;
          this.districtName = ulm.getDistrictName();
          this.siteName = ulm.getLocationName();
          this.siteGuid = ulm.getSiteGuid();
        } else {
          this.showLogin = true;
          this.showUserProfile = false;
          this.showAppSwitcher = false;
          this.districtUserType = false;
          this.isGuestUser = true;
          this.isShared = false;
          this.siteGuid = undefined;
        }
      });

    this.userService.getDistrictInfo$()
      .pipe(
        filter(d => !!d),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((di: DistrictModel) =>  {
        this.districtName = di?.districtName;
      });

    this.themeService.getCurrentTheme$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((theme: any) => {
        this.originalStyles = theme;
        this.applyThemesForApp(this.portalStateService.getActiveApp());
      });


    this.portalStateService.getActiveApp$()
      .pipe(
        filter(x => !!x),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((app: PortalApp) => {
        this.currentApp = app?.app;
        this.applyThemesForApp(app);
      });

    this.clientLoginService.getPopupState$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe( state => {
        this.isLoginPopupDisplayed = ( state === PopupState.LOGIN );
      });

    this.portalStateService.getMenuState$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.applyThemesForApp(this.portalStateService.getActiveApp());
      });
  }

  public isMenuToggleEnabled() {
    return [SidenavMenuStates.STANDARD, SidenavMenuStates.HIDDEN].indexOf(this.portalStateService.getMenuStateForApp()) >= 0 && this.isMobile;
  }

  public isSidenavOpen() {
    return this.portalStateService.getMenuStateForApp() === SidenavMenuStates.STANDARD;
  }

  public goToDistrictHomePage() {
    this.portalStateService.goToDistrictHomePage();
  }

  public isShowingHomePageButton() {
    return this.authService.getUserLogin()?.isDistrictUser() || this.authService.getUserLogin()?.isDestinyAdmin();
  }

  private observeScreenSizeChanges() {
    this.breakpointObserver
      .observe(Object.values(ItemsToHide))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        this.isHiddingMyInfo = result.breakpoints[ItemsToHide.MyInfo];
        this.isHiddingDashboard = result.breakpoints[ItemsToHide.Dashboard];
        this.isHiddingHome = result.breakpoints[ItemsToHide.Home];
      });
  }

  private applyThemesForApp(app: PortalApp) {
    if (this.portalStateService.isUnEnabledForCurrentApp()) {
      document.documentElement.style.cssText = '';
      if (this.originalStyles && (app?.app === Application.DD || app?.app === Application.COLLECTIONS)) {
        for (const property in NewUNColors) {
          switch (property) {
            case '--platform-500':
            case '--base-white':
              document.documentElement.style.setProperty(property, this.originalStyles['--headerPrimaryFill-color']);
              break;
            case '--platform-400':
              document.documentElement.style.setProperty(property, this.originalStyles['--headerButtonBackground-color']);
              break;
            case '--platform-800':
            case '--platform-300':
            case '--f-blue-500':
            case '--base-white-text':
              document.documentElement.style.setProperty(property, this.originalStyles['--headerSmallMediumText-color']);
              break;
            case '--platform-900':
              document.documentElement.style.setProperty(property, this.originalStyles['--headerAccent-color']);
              break;
            case '--secondary-header':
            case '--platform-50':
            case '--platform-100':
            case '--platform-200':
              document.documentElement.style.setProperty(property, this.originalStyles['--secondaryHeaderAndHover-color']);
              break;
            default:
              document.documentElement.style.setProperty(property, NewUNColors[property as keyof typeof NewUNColors]);
              break;
          }
        }
      } else {
        for (const property in NewUNColors) {
          document.documentElement.style.setProperty(property, NewUNColors[property as keyof typeof NewUNColors]);
        }
      }
    } else {
      if (this.originalStyles && (app?.app === Application.DD || app?.app === Application.COLLECTIONS)) {
        document.documentElement.style.cssText = '';
        for (const property in this.originalStyles) {
          document.documentElement.style.setProperty(property, this.originalStyles[property]);
        }
      } else {
        document.documentElement.style.cssText = '';
        for (const property in DestinyColors) {
          document.documentElement.style.setProperty(property, DestinyColors[property as keyof typeof DestinyColors]);
        }
      }
    }
  }

  public toggleMobileMenu() {
    this.toolbarService.isMobileMenuOpen = !this.toolbarService.isMobileMenuOpen;
  }

  public toggleSidenav() {
    this.toolbarService.toggleSidenav();
  }

  public getMenuItems(): MenuItem[] {
    if (this.currentApp !== Application.DESTINY) {
      return this.portalStateService.activeMenuItems;
    }

    return this.getMenuItemsForDestiny(
      this.portalStateService.activeMenuItems,
      this.isHiddingMyInfo,
      this.isHiddingDashboard,
      this.isHiddingHome
    );
  }

  /**
   * We are memoizing this function, not because it's complex, but because we are cloning and
   * mutating the array, and returning a new reference triggers the Angular change detention.
   *
   * We are stringifying the arguments to be used as the resolver by memoize because it will only
   * use the first argument as a key for the cache.
   */
  private getMenuItemsForDestiny = _.memoize(
    (
      activeMenuItems: MenuItem[],
      isHiddingMyInfo: boolean,
      isHiddingDashboard: boolean,
      isHiddingHome: boolean
    ): MenuItem[] => {
      const menuItems = _.cloneDeep(activeMenuItems);
      const moreItem = menuItems.find(({ id }) => id === 'MoreMenu');
      if (moreItem == null) {
        return menuItems;
      }
      moreItem.subMenuItems = moreItem.subMenuItems ?? [];

      if (isHiddingMyInfo) {
        const myInfoItem = menuItems.find(({ id }) => id === 'TopLevelMyInfo');
        if (myInfoItem) {
          _.remove(menuItems, (item) => item === myInfoItem);
          moreItem.subMenuItems = [myInfoItem, ...moreItem.subMenuItems];
        }
      }

      if (isHiddingDashboard) {
        const dashboardItem = menuItems.find(({ id }) => id === 'TopLevelDashboard');
        if (dashboardItem) {
          _.remove(menuItems, (item) => item === dashboardItem);
          moreItem.subMenuItems = [dashboardItem, ...moreItem.subMenuItems];
        }
      }

      if (isHiddingHome) {
        const homeItem = menuItems.find(({ id }) => id === 'Home');
        if (homeItem) {
          _.remove(menuItems, (item) => item === homeItem);
          moreItem.subMenuItems = [homeItem, ...moreItem.subMenuItems];
        }
      }

      return menuItems;
    },    (...keys) => {
      return this.calculateMenuKey(keys);
    }
  );

  public calculateMenuKey(keys: any[]): string{
    const isHiddingMyInfo:boolean = keys[1];
    const isHiddingDashboard:boolean = keys[2];
    const isHiddingHome: boolean = keys[3];
    const items:MenuItem[] = keys[0];
    const itemIds: string = items.map(item => this.menuKeys(item)).join('');
    const key = itemIds + isHiddingDashboard + isHiddingHome + isHiddingMyInfo;
    return key;
  }

  public menuKeys(item:MenuItem){
    let key = item.id + item.name;
    if(item.subMenuItems){
      // for each submenu, call menuKeys
      key += item.subMenuItems.map(subitem => this.menuKeys(subitem)).join('');
    }
    return key;

  }

  public login() {
    this.toolbarService.login();
  }

  public showSiteSwitcher() {
    if (this.authService.isDestinyForcePasswordReset()) {
      return false;
    } else {
        return (this.districtUserType ? (this.sitesAvailable > 0) : (this.sitesAvailable > 1));
      }
  }

  public getLogoUrl() {
    const districtSettings = this.unifiedSettingsService.getUnifiedDistrictSettings();
    if (this.authService.getSiteGuid() && districtSettings?.getSiteLogo(this.authService.getSiteGuid())) {
      return districtSettings?.getSiteLogo(this.authService.getSiteGuid());
    } else {
      return districtSettings?.getDistrictLogo();
    }
  }

  public isDDorCollections() {
    return this.portalStateService.getActiveApp().app === Application.DD || this.portalStateService.getActiveApp().app === Application.COLLECTIONS;
  }
}
